import { Chip, Typogaphy } from 'vnetwork-ui'
import { useTranslation } from 'react-i18next'
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion'
import { CVAnalysisResult } from '../../../types'

interface Props {
    analysis: CVAnalysisResult
}

// Renders the structured CV analysis returned by /cv/analyze.
// Sections collapse independently so reviewers can focus on what they care about.
const CVAnalysisDisplay = ({ analysis }: Props) => {
    const { t: tResume } = useTranslation('resume')
    const { profile, skills, experience, education, evaluation } = analysis

    return (
        <Accordion
            type="multiple"
            defaultValue={evaluation ? ['evaluation', 'profile'] : ['profile']}
            className="px-2 space-y-1"
        >
            {evaluation && (
                <AccordionItem
                    value="evaluation"
                    className="border-b border-gray-100"
                >
                    <AccordionTrigger className="py-1.5 hover:no-underline">
                        <div className="flex items-center gap-2">
                            <Typogaphy className="font-medium text-sm text-gray-700">
                                {tResume('ai-evaluation')}
                            </Typogaphy>
                            <Chip
                                label={`${evaluation.score}/10`}
                                color={chipColorForScore(evaluation.score)}
                                size="small"
                                className="font-bold"
                            />
                            <Chip
                                label={evaluation.recommendation}
                                color={chipColorForRecommendation(
                                    evaluation.recommendation
                                )}
                                size="small"
                            />
                        </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-2">
                        <ChipList
                            label={tResume('ai-matches')}
                            items={evaluation.matches}
                            color="success"
                        />
                        <ChipList
                            label={tResume('ai-gaps')}
                            items={evaluation.gaps}
                            color="warning"
                        />
                        <BulletList
                            label={tResume('ai-strengths')}
                            items={evaluation.strengths}
                        />
                        <BulletList
                            label={tResume('ai-weaknesses')}
                            items={evaluation.weaknesses}
                        />
                        {evaluation.summary && (
                            <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed bg-blue-50 p-3 rounded-md border border-blue-200">
                                <Typogaphy className="text-sm font-medium text-gray-600 mb-1">
                                    {tResume('ai-summary')}:
                                </Typogaphy>
                                {evaluation.summary}
                            </div>
                        )}
                        <BulletList
                            label={tResume('ai-interview-questions')}
                            items={evaluation.interview_questions}
                            ordered
                        />
                    </AccordionContent>
                </AccordionItem>
            )}

            <AccordionItem value="profile" className="border-b border-gray-100">
                <AccordionTrigger className="py-1.5 hover:no-underline">
                    <Typogaphy className="font-medium text-sm text-gray-700">
                        {tResume('ai-profile')} ·{' '}
                        {profile?.years_experience
                            ? `${profile.years_experience} ${tResume('years')}`
                            : '-'}
                    </Typogaphy>
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                    {profile?.summary && (
                        <Typogaphy className="text-sm text-gray-700 whitespace-pre-wrap">
                            {profile.summary}
                        </Typogaphy>
                    )}
                    <ChipList
                        label={tResume('ai-domains')}
                        items={profile?.domains || []}
                    />
                    <ChipList
                        label={tResume('ai-primary-stack')}
                        items={profile?.primary_stack || []}
                        color="info"
                    />
                </AccordionContent>
            </AccordionItem>

            {skills?.length > 0 && (
                <AccordionItem
                    value="skills"
                    className="border-b border-gray-100"
                >
                    <AccordionTrigger className="py-1.5 hover:no-underline">
                        <Typogaphy className="font-medium text-sm text-gray-700">
                            {tResume('ai-skills')} ({skills.length})
                        </Typogaphy>
                    </AccordionTrigger>
                    <AccordionContent>
                        <div className="flex flex-wrap gap-1">
                            {skills.map((s) => (
                                <Chip
                                    key={`${s.name}-${s.category}`}
                                    label={`${s.name} · L${s.level}${
                                        s.years ? ` · ${s.years}y` : ''
                                    }`}
                                    size="small"
                                    color={chipColorForSkillLevel(s.level)}
                                    className="text-xs"
                                />
                            ))}
                        </div>
                    </AccordionContent>
                </AccordionItem>
            )}

            {experience?.length > 0 && (
                <AccordionItem
                    value="experience"
                    className="border-b border-gray-100"
                >
                    <AccordionTrigger className="py-1.5 hover:no-underline">
                        <Typogaphy className="font-medium text-sm text-gray-700">
                            {tResume('ai-experience')} ({experience.length})
                        </Typogaphy>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-2">
                        {experience.map((e) => (
                            <div
                                key={`${e.company}-${e.title}-${e.start_date}`}
                                className="border-l-2 border-emerald-300 pl-3 py-1"
                            >
                                <div className="text-sm font-medium text-gray-800">
                                    {e.title} @ {e.company}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {e.start_date} - {e.end_date}
                                </div>
                                {e.description && (
                                    <div className="text-sm text-gray-700 mt-1">
                                        {e.description}
                                    </div>
                                )}
                                {e.achievements?.length > 0 && (
                                    <ul className="list-disc list-inside text-sm text-gray-700 mt-1">
                                        {e.achievements.map((a) => (
                                            <li key={a}>{a}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </AccordionContent>
                </AccordionItem>
            )}

            {education?.length > 0 && (
                <AccordionItem
                    value="education"
                    className="border-b border-gray-100"
                >
                    <AccordionTrigger className="py-1.5 hover:no-underline">
                        <Typogaphy className="font-medium text-sm text-gray-700">
                            {tResume('ai-education')} ({education.length})
                        </Typogaphy>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-1">
                        {education.map((e) => (
                            <div
                                key={`${e.school}-${e.degree}-${e.start_date}`}
                                className="text-sm text-gray-700"
                            >
                                <span className="font-medium">{e.school}</span>
                                {e.degree && ` · ${e.degree}`}
                                {e.major && ` · ${e.major}`}
                                <span className="text-xs text-gray-500 ml-2">
                                    {e.start_date} - {e.end_date}
                                </span>
                                {e.gpa && (
                                    <span className="text-xs text-gray-500 ml-2">
                                        GPA: {e.gpa}
                                    </span>
                                )}
                            </div>
                        ))}
                    </AccordionContent>
                </AccordionItem>
            )}
        </Accordion>
    )
}

// chipColorForScore picks a traffic-light color based on the AI overall score.
const chipColorForScore = (score: number) => {
    if (score >= 7) return 'success'
    if (score >= 5) return 'warning'
    return 'error'
}

// chipColorForRecommendation maps the LLM hire/conditional/reject verdict to chip color.
const chipColorForRecommendation = (rec: string) => {
    if (rec === 'hire') return 'success'
    if (rec === 'conditional') return 'warning'
    if (rec === 'reject') return 'error'
    return 'default'
}

// chipColorForSkillLevel maps proficiency 1-5 to chip color so reviewers scan strengths quickly.
const chipColorForSkillLevel = (level: number) => {
    if (level >= 4) return 'success'
    if (level >= 3) return 'info'
    if (level >= 2) return 'warning'
    return 'default'
}

const ChipList = ({
    label,
    items,
    color = 'default',
}: {
    label: string
    items: string[]
    color?: 'success' | 'warning' | 'error' | 'info' | 'default'
}) => {
    if (!items?.length) return null
    return (
        <div>
            <Typogaphy className="text-sm font-medium text-gray-600 mb-1">
                {label}:
            </Typogaphy>
            <div className="flex flex-wrap gap-1">
                {items.map((item, i) => (
                    <Chip
                        key={`${item}-${i}`}
                        label={item}
                        size="small"
                        color={color}
                        className="text-xs"
                    />
                ))}
            </div>
        </div>
    )
}

const BulletList = ({
    label,
    items,
    ordered = false,
}: {
    label: string
    items: string[]
    ordered?: boolean
}) => {
    if (!items?.length) return null
    const ListTag = ordered ? 'ol' : 'ul'
    const listClass = ordered
        ? 'list-decimal list-inside text-sm text-gray-700 space-y-0.5'
        : 'list-disc list-inside text-sm text-gray-700 space-y-0.5'
    return (
        <div>
            <Typogaphy className="text-sm font-medium text-gray-600 mb-1">
                {label}:
            </Typogaphy>
            <ListTag className={listClass}>
                {items.map((it) => (
                    <li key={it}>{it}</li>
                ))}
            </ListTag>
        </div>
    )
}

export default CVAnalysisDisplay
